/**
 * The versioned save envelope (tech-design §5): `{ formatVersion, engineVersion,
 * savedAt, checksum, state }`. The envelope is what actually lands in IndexedDB and
 * in an exported blob; {@link encodeSave}/{@link decodeSave} are the pure boundary
 * between a live {@link GameState} and that on-disk shape.
 *
 * `decodeSave` **never throws**: a corrupt (checksum-mismatched), unknown-future, or
 * un-migratable envelope yields a fresh state so a caller can decide to fall back to
 * the backup ring rather than crash. {@link tryDecode} exposes the same logic but
 * returns `null` on failure, which is what the DB layer uses to drive that fallback.
 */
import { SAVE_FORMAT_VERSION, createInitialState, type GameState } from '@core/index.js';
import { deserializeState, serializeState, type SerializedGameState } from './codec.js';
import { CURRENT_FORMAT_VERSION, migrate } from './migrations.js';

/** Identifies the engine build that wrote a save (diagnostics / future gating). */
export const ENGINE_VERSION = '0.1.0';

export interface SaveEnvelope {
  readonly formatVersion: number;
  readonly engineVersion: string;
  readonly savedAt: number;
  /** Deterministic hash of the serialized state — detects a corrupted blob. */
  readonly checksum: string;
  readonly state: SerializedGameState;
}

// ── Checksum ──────────────────────────────────────────────────────────────────

/**
 * Canonical JSON with recursively sorted object keys, so the checksum is stable
 * regardless of the key order a structured-clone or JSON round-trip happens to
 * preserve.
 */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }
  if (value !== null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(record).sort()) {
      sorted[key] = canonicalize(record[key]);
    }
    return sorted;
  }
  return value;
}

/** cyrb53 — a fast, well-distributed non-cryptographic string hash (hex). */
function cyrb53(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const hash = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return hash.toString(16);
}

/** The checksum of a serialized state — recomputed on load to detect corruption. */
export function checksumOf(state: SerializedGameState): string {
  return cyrb53(JSON.stringify(canonicalize(state)));
}

// ── Encode / decode ─────────────────────────────────────────────────────────

/** Wrap a live state in a versioned, checksummed envelope. Pure — no I/O. */
export function encodeSave(state: GameState, savedAt: number): SaveEnvelope {
  const serialized = serializeState(state);
  return {
    formatVersion: SAVE_FORMAT_VERSION,
    engineVersion: ENGINE_VERSION,
    savedAt,
    checksum: checksumOf(serialized),
    state: serialized,
  };
}

/**
 * Recover a live state from an envelope, or `null` if it can't be trusted:
 * a version newer than this build understands, a checksum mismatch (corruption),
 * or a migration gap. Older-but-known formats are migrated forward first.
 */
export function tryDecode(envelope: SaveEnvelope): GameState | null {
  if (
    typeof envelope.formatVersion !== 'number' ||
    envelope.formatVersion > CURRENT_FORMAT_VERSION
  ) {
    return null;
  }
  // A present checksum must match; envelopes predating checksums (none shipped) skip.
  if (envelope.checksum && envelope.checksum !== checksumOf(envelope.state)) {
    return null;
  }
  try {
    return deserializeState(migrate(envelope.state, envelope.formatVersion));
  } catch {
    return null;
  }
}

/** Like {@link tryDecode} but never null: an untrusted envelope yields a fresh state. */
export function decodeSave(envelope: SaveEnvelope): GameState {
  return tryDecode(envelope) ?? createInitialState();
}
